import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Plus } from 'lucide-react';
import { Button } from '../ui/button';
import { OfficeCard } from '../offices/OfficeCard';
import { OfficeForm } from '../offices/OfficeForm';
import { OfficeStats } from '../offices/OfficeStats';
import { errMsg } from './adminOfficesApi';
import { PlanAndLimitControls } from './PlanAndLimitControls';
import { StaffOfficeList } from './StaffOfficeList';
import { MovePatientsTool } from './MovePatientsTool';
import { useConfirm } from '../useConfirm';

export const PracticeOfficesDetail = ({ pid, api, onChanged }) => {
  const [d, setD] = useState(null);
  const [adding, setAdding] = useState(false);
  const [confirmDialog, confirm] = useConfirm();
  const load = useCallback(async () => {
    try { setD(await api.practice(pid)); } catch (e) { toast.error(errMsg(e)); }
  }, [api, pid]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, msg) => {
    try { const r = await fn(); toast.success(typeof msg === 'function' ? msg(r) : msg); await load(); onChanged(); return true; }
    catch (e) { toast.error(errMsg(e)); return false; }
  };
  if (!d) return <p className="px-4 pb-4 text-slate-500">Loading…</p>;
  const open = d.offices.filter((o) => o.isActive !== false);
  return (
    <div className="px-4 pb-6 pt-2 bg-slate-50/70 space-y-5" data-testid={`offices-admin-detail-${pid}`}>
      {confirmDialog}
      <PlanAndLimitControls key={`${d.practice.tier}-${d.practice.override}-${d.practice.status}`} practice={d.practice} openCount={open.length} act={act} api={api} confirm={confirm} />
      <div className="rounded-xl bg-slate-900 p-4 space-y-2">
        <p className="text-white text-sm font-semibold">All offices combined</p>
        <OfficeStats stats={d.combined} testId={`admin-combined-stats-${pid}`} dark />
      </div>
      <div className="flex justify-between items-center">
        <h4 className="font-semibold">Offices ({open.length} open)</h4>
        <Button size="sm" onClick={() => setAdding(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`admin-add-office-${pid}`}><Plus className="w-4 h-4 mr-1" />Add office</Button>
      </div>
      {adding && (
        <div className="bg-white border rounded-xl p-4">
          <OfficeForm testId="admin-office-add-form" saveLabel="Add office" onCancel={() => setAdding(false)}
            onSave={async (f) => { if (await act(() => api.addOffice(pid, f), `${f.name} added`)) setAdding(false); }} />
        </div>
      )}
      {d.offices.map((o) => (
        <OfficeCard key={o.id} office={o} isAdmin
          onEdit={(x, f) => act(() => api.editOffice(pid, x.id, f), 'Office saved')}
          onMakeMain={(x) => act(() => api.editOffice(pid, x.id, { makePrimary: true }), `${x.name} is now the main office`)}
          onClose={async (x) => (await confirm({ title: `Close ${x.name}?`, text: 'Its patients are archived (not moved or deleted) and its staff logins are turned off. Reopen any time to restore everything.', ok: 'Close office', danger: true }))
            && act(() => api.closeOffice(pid, x.id), (r) => `${x.name} closed · ${r.archivedPatients} patients archived`)}
          onReopen={(x) => act(() => api.reopenOffice(pid, x.id), (r) => `${x.name} reopened · ${r.restoredPatients} patients restored`)} />
      ))}
      {open.length > 1 && <MovePatientsTool offices={open} confirm={confirm} onMove={(from, to) => act(() => api.movePatients(pid, to, from), (r) => `${r.moved} patients moved`)} />}
      <StaffOfficeList staff={d.staff} offices={open} onSet={(uid, oid) => act(() => api.setStaffOffice(pid, uid, oid || null), 'Staff office access updated')} />
    </div>
  );
};
